<template>
  <nav class="tenant-navbar" role="navigation" :aria-label="brandName" :style="brandStyle">
    <router-link :to="{ name: 'home' }" class="brand-link">
      <img v-if="logoUrl" class="brand-logo" :src="logoUrl" :alt="brandName" />

      <!--
        Nothing is rendered while the request is in flight. `brandName` falls back to
        the platform name, and painting that for the duration of the round trip would
        show every visitor the apex brand's name for a frame before swapping it for
        the clinic's -- the exact bug the per-host bootstrap payload existed to avoid.
      -->
      <span
        v-if="loading"
        class="brand-skeleton"
        data-test="navbar-skeleton"
        aria-hidden="true"
      ></span>
      <span v-else class="brand-name" :title="seoTitle">{{ brandName }}</span>
    </router-link>
  </nav>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { fetchCurrentTenant, type CurrentTenant } from '@/apis/tenant.api';

// What the navbar shows when the endpoint cannot answer. On the apex that string is
// also the correct name, so the failure state and the platform state agree instead of
// leaving the bar blank.
const PLATFORM_NAME = 'BioTerapias.cl';

// Six hex digits, optionally prefixed. Deliberately stricter than the CSS the value
// is interpolated into: this string arrives over the network from per-host tenant
// data, and anything that is not a plain colour must never reach a style attribute.
const HEX_COLOR = /^#?[0-9a-fA-F]{6}$/;

const tenant = ref<CurrentTenant | null>(null);
const loading = ref(true);

const brandName = computed(() => tenant.value?.name?.trim() || PLATFORM_NAME);

const seoTitle = computed(() => tenant.value?.seo?.title?.trim() || brandName.value);

const logoUrl = computed(() => tenant.value?.settings?.logo_url || null);

const brandColor = computed(() => {
  const settings = tenant.value?.settings;
  if (!settings) return null;
  return firstValidColor(settings.primary_color, settings.secondary_color);
});

function firstValidColor(...candidates: (string | null | undefined)[]): string | null {
  for (const candidate of candidates) {
    if (!candidate) continue;
    const hex = candidate.trim();
    if (HEX_COLOR.test(hex)) return hex.startsWith('#') ? hex : `#${hex}`;
  }
  return null;
}

const brandStyle = computed(() => {
  const color = brandColor.value;
  return color ? { '--brand-primary': color } : {};
});

onMounted(async () => {
  try {
    const { data } = await fetchCurrentTenant();
    tenant.value = data;
  } catch {
    // A navbar is chrome: it must render whatever it has. Swallowing the failure here
    // is the whole point -- `brandName` already falls back to the platform name.
    tenant.value = null;
  } finally {
    loading.value = false;
  }
});
</script>

<style scoped>
.tenant-navbar {
  background: var(--brand-primary, #100414);
  border-bottom: 1px solid rgb(255 255 255 / 8%);
  padding: 0.9rem 1.25rem;
}

.navbar-brand,
.brand-link {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}

.brand-skeleton {
  display: inline-block;
  height: 1.1rem;
  width: 9rem;
  border-radius: 3px;
  background: rgb(255 255 255 / 18%);
}

.brand-link {
  color: #fff;
  font-size: 1.05rem;
  font-weight: 600;
  text-decoration: none;
}

.brand-logo {
  height: 2rem;
  width: 2rem;
  border-radius: 4px;
  object-fit: cover;
}

.brand-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
